import React, { useState, useEffect } from 'react';
import { useTaskItemStore } from '../../stores/useTaskItemStore';
import { useTaskListStore } from '../../stores/useTaskListStore';
import { TaskItemTiptapEditor } from '../TaskItemTiptapEditor/TaskItemTiptapEditor';
import {
  Box,
  Flex,
  Heading,
  Text,
  Button,
  IconButton,
  Badge,
} from '@radix-ui/themes';
import { ArrowLeftIcon } from '@radix-ui/react-icons';
import styles from './DetailedPanel.module.css';
import { Fireworks } from '../../components/Fireworks/Fireworks';
import { DEFAULT_TASK_TITLE } from '../../utilities';
import { useDroppable } from '@dnd-kit/core';

interface DetailedPanelProps {
  onClose?: () => void;
  activeDragId?: string | null;
}

const DISMISS_SLUGS = [
  'Cool, back to work! :D',
  'Yeah!',
  'Focus is a muscle.',
  'One step closer!',
  "Let's see what's next.",
  'Success!',
  'Awesome!',
  'Done and dusted.',
  'Boom!',
  'Great job!',
  'That felt good.',
  'Onto the next one!',
  'Productive!',
  'Checked off!',
  'Clean slate.',
  'Keep it up!',
  'Task accomplished.',
  'Victory!',
  "Let's keep building.",
  'Nice one!',
];

interface BubbleProps {
  onClick?: () => void;
  className?: string;
  headerElement?: React.ReactNode;
  tailElement?: React.ReactNode;
  animateHeader?: boolean;
  animateTail?: boolean;
  children: React.ReactNode;
  asButton?: boolean;
}

const Bubble: React.FC<BubbleProps> = ({
  onClick,
  className,
  headerElement,
  tailElement,
  animateHeader = false,
  animateTail = false,
  children,
  asButton = false,
}) => {
  const content = (
    <>
      {headerElement && (
        <span
          className={`${styles.bubbleEmoji} ${animateHeader ? styles.jumpingEmoji : ''}`}
        >
          {headerElement}
        </span>
      )}
      <span className={styles.bubbleContent}>{children}</span>
      {tailElement && (
        <span
          className={`${styles.bubbleEmoji} ${animateTail ? styles.jumpingEmoji : ''}`}
        >
          {tailElement}
        </span>
      )}
    </>
  );

  if (asButton) {
    return (
      <button onClick={onClick} className={className}>
        {content}
      </button>
    );
  }

  return <div className={className}>{content}</div>;
};

export const DetailedPanel: React.FC<DetailedPanelProps> = ({
  onClose,
  activeDragId,
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id: 'detailed-panel-drop-zone',
  });

  const activeItemId = useTaskItemStore((s) => s.activeItemId);
  const setActiveItemId = useTaskItemStore((s) => s.setActiveItemId);

  const itemsMap = useTaskListStore((s) => s.itemsMap);
  const legalItemIds = Object.values(itemsMap).flat();

  const isLegal = activeItemId ? legalItemIds.includes(activeItemId) : false;

  // Auto-clean active selection if the item becomes illegal (e.g. deleted)
  useEffect(() => {
    if (activeItemId && !isLegal) {
      setActiveItemId(null);
    }
  }, [activeItemId, isLegal]);

  const rawItem = useTaskItemStore((s) => s.items)[activeItemId || ''];
  const item =
    isLegal && activeItemId
      ? rawItem || {
          id: activeItemId,
          title: DEFAULT_TASK_TITLE,
          content: '',
          completed: false,
        }
      : null;
  const updateContent = useTaskItemStore((s) => s.updateContent);
  const toggleComplete = useTaskItemStore((s) => s.toggleComplete);
  const clearJustCompleted = useTaskItemStore((s) => s.clearJustCompleted);
  const toggleFail = useTaskItemStore((s) => s.toggleFail);
  const clearJustFailed = useTaskItemStore((s) => s.clearJustFailed);

  const [dismissSlug, setDismissSlug] = useState('');

  // Pick a random dismiss slug once when task is completed
  useEffect(() => {
    if (item && item.completed && item.justCompleted) {
      const randomIndex = Math.floor(Math.random() * DISMISS_SLUGS.length);
      setDismissSlug(DISMISS_SLUGS[randomIndex]);
    }
  }, [activeItemId, item?.completed, item?.justCompleted]);

  // Render empty state if no item is selected or active
  if (!item) {
    return (
      <Flex
        // Attach setNodeRef here so Dnd-kit registers this empty area as a valid droppable drop zone
        ref={setNodeRef}
        direction="column"
        className={`${styles.emptyContainer} ${isOver ? styles.isOver : ''}`}
        align="center"
        justify="center"
        style={{ width: '100%', height: '100%' }}
      >
        {/* Only show the drop-to-complete indicator if a task card is being dragged and hovered directly over this panel */}
        {activeDragId && isOver && (
          <Flex
            direction="column"
            align="center"
            gap="3"
            style={{ textAlign: 'center', padding: '24px' }}
          >
            <span style={{ fontSize: '48px', marginBottom: '8px' }}>📥</span>
            <Heading size="4" color="green">
              Drag here to complete
            </Heading>
            <Text color="gray" size="2">
              Drop here to complete the task
            </Text>
          </Flex>
        )}
      </Flex>
    );
  }

  if (item.failed && item.justFailed) {
    return (
      <div
        ref={setNodeRef}
        className={`${styles.failedPanelContainer} ${isOver ? styles.isOver : ''}`}
      >
        <div className={styles.chatCelebrationFeed}>
          {/* Failed Task Title (Animated strike-through but red) */}
          <Heading size="5" className={styles.failedTitleHeader}>
            <span className={styles.failedTitleWrapper}>
              <svg className={styles.crossSvg} viewBox="0 0 24 24">
                <line
                  x1="18"
                  y1="6"
                  x2="6"
                  y2="18"
                  className={styles.crossLine}
                />
                <line
                  x1="6"
                  y1="6"
                  x2="18"
                  y2="18"
                  className={styles.crossLine}
                />
              </svg>
              <span className={styles.failedTitle}>
                {item.title || DEFAULT_TASK_TITLE}
              </span>
            </span>
          </Heading>

          {/* Assistant completed message bubble */}
          <Bubble
            className={styles.celebrationAiMessage}
            headerElement="😔"
            animateHeader={false}
          >
            Too bad, this task failed. Don't worry, there's always a next one.
          </Bubble>

          {/* User actions group (closer spacing) */}
          <div className={styles.userActionsWrapper}>
            {/* User primary dismiss action bubble */}
            <Bubble
              asButton
              className={styles.celebrationUserMessage}
              onClick={() => clearJustFailed(item.id)}
              tailElement="🏃"
              animateTail={true}
            >
              Okay, let's keep moving.
            </Bubble>

            {/* User secondary undo action bubble */}
            <Bubble
              asButton
              className={styles.celebrationUndoMessage}
              onClick={() => toggleFail(item.id)}
            >
              Undo
            </Bubble>
          </div>
        </div>
      </div>
    );
  }

  if (item.completed && item.justCompleted) {
    return (
      <div
        ref={setNodeRef}
        className={`${styles.completedPanelContainer} ${isOver ? styles.isOver : ''}`}
      >
        <Fireworks />
        <div className={styles.chatCelebrationFeed}>
          {/* Completed Task Title (Animated strike-through) */}
          <Heading size="5" className={styles.completedTitleHeader}>
            <span className={styles.completedTitleWrapper}>
              <svg className={styles.tickSvg} viewBox="0 0 24 24">
                <polyline className={styles.tickPath} points="4 12 9 17 20 6" />
              </svg>
              <span className={styles.completedTitle}>
                {item.title || DEFAULT_TASK_TITLE}
              </span>
            </span>
          </Heading>

          {/* Assistant completed message bubble */}
          <Bubble
            className={styles.celebrationAiMessage}
            headerElement="🎉"
            animateHeader={true}
          >
            Amazing! You have completed this task.
          </Bubble>

          {/* User actions group (closer spacing) */}
          <div className={styles.userActionsWrapper}>
            {/* User primary dismiss action bubble */}
            <Bubble
              asButton
              className={styles.celebrationUserMessage}
              onClick={() => clearJustCompleted(item.id)}
              tailElement="🚀"
              animateTail={true}
            >
              {dismissSlug || "Well done. Let's see what's next"}
            </Bubble>

            {/* User secondary undo action bubble */}
            <Bubble
              asButton
              className={styles.celebrationUndoMessage}
              onClick={() => toggleComplete(item.id)}
            >
              Undo
            </Bubble>
          </div>
        </div>
      </div>
    );
  }

  const handleContentUpdate = (markdown: string) => {
    updateContent(item.id, markdown, undefined);
  };

  return (
    <Flex
      ref={setNodeRef}
      direction="column"
      className={`${styles.panelContainer} ${isOver ? styles.isOver : ''}`}
    >
      {/* Header with Back button for mobile & Restore button */}
      <Flex
        align="center"
        justify="between"
        className={styles.header}
        style={{ width: '100%' }}
      >
        <Flex align="center" gap="3">
          <IconButton
            variant="ghost"
            onClick={onClose}
            className={styles.backButton}
          >
            <ArrowLeftIcon width="20" height="20" />
          </IconButton>
          {item.deleted && (
            <Badge color="red" variant="soft">
              Deleted Task
            </Badge>
          )}
        </Flex>
        {item.deleted && (
          <Button
            size="2"
            variant="solid"
            color="green"
            onClick={() => {
              useTaskItemStore.getState().restoreItem(item.id);
            }}
            style={{ cursor: 'pointer' }}
          >
            Restore Task
          </Button>
        )}
      </Flex>

      {/* Markdown Document Editor */}
      <Box className={styles.editorContainer}>
        <TaskItemTiptapEditor
          key={item.id}
          value={item.content || ''}
          placeholder="Please add content here.."
          updateContent={handleContentUpdate}
          updateIsEditing={() => {}}
          autofocus={!item.content || !item.content.trim() ? 'start' : false}
          readOnly={item.deleted}
        />
      </Box>
    </Flex>
  );
};
